import { useCallback, useMemo } from "react";
import { useStoredState, uid } from "./storage";

/* ---------------------------------------------------------------- Shopping */
export const SHOPPING_KEY = "recify_shopping";

export function useShoppingList() {
  const [items, setItems] = useStoredState(SHOPPING_KEY, []);
  const list = Array.isArray(items) ? items : [];

  /** Adds ingredient lines for a recipe, skipping ones that are already on the list. */
  const addMany = useCallback(
    (recipeId, recipeTitle, texts) => {
      let added = 0;
      setItems((prev) => {
        const current = Array.isArray(prev) ? prev : [];
        const seen = new Set(current.map((i) => `${i.recipeId}|${i.text}`));
        const fresh = texts
          .filter((t) => t && !seen.has(`${recipeId}|${t}`))
          .map((text) => ({ id: uid(), text, recipeId, recipe: recipeTitle, checked: false }));
        added = fresh.length;
        return [...current, ...fresh];
      });
      return added;
    },
    [setItems]
  );

  const addCustom = useCallback(
    (text) => addMany("custom", "My items", [text.trim()]),
    [addMany]
  );

  const toggle = useCallback(
    (id) =>
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, checked: !i.checked } : i))),
    [setItems]
  );
  const remove = useCallback(
    (id) => setItems((prev) => prev.filter((i) => i.id !== id)),
    [setItems]
  );
  const clearChecked = useCallback(
    () => setItems((prev) => prev.filter((i) => !i.checked)),
    [setItems]
  );
  const clearAll = useCallback(() => setItems([]), [setItems]);

  return { items: list, addMany, addCustom, toggle, remove, clearChecked, clearAll };
}

/* ------------------------------------------------------------- Collections */
export function useCollections() {
  const [raw, setRaw] = useStoredState("recify_collections", []);
  const collections = Array.isArray(raw) ? raw : [];

  const create = useCallback(
    (name, ids = []) => {
      const id = uid();
      setRaw((prev) => [...prev, { id, name: name.trim(), ids: ids.map(String) }]);
      return id;
    },
    [setRaw]
  );
  const rename = useCallback(
    (id, name) => setRaw((prev) => prev.map((c) => (c.id === id ? { ...c, name: name.trim() } : c))),
    [setRaw]
  );
  const remove = useCallback(
    (id) => setRaw((prev) => prev.filter((c) => c.id !== id)),
    [setRaw]
  );
  const addIds = useCallback(
    (id, ids) =>
      setRaw((prev) =>
        prev.map((c) =>
          c.id === id ? { ...c, ids: Array.from(new Set([...c.ids, ...ids.map(String)])) } : c
        )
      ),
    [setRaw]
  );
  const removeIds = useCallback(
    (id, ids) => {
      const drop = new Set(ids.map(String));
      setRaw((prev) =>
        prev.map((c) => (c.id === id ? { ...c, ids: c.ids.filter((x) => !drop.has(x)) } : c))
      );
    },
    [setRaw]
  );

  return { collections, create, rename, remove, addIds, removeIds };
}

/* ------------------------------------------------------------ Notes/ratings */
export function useRecipeNotes() {
  const [raw, setRaw] = useStoredState("recify_notes", {});
  const notes = useMemo(() => (raw && typeof raw === "object" ? raw : {}), [raw]);

  const setNote = useCallback(
    (id, patch) =>
      setRaw((prev) => ({ ...prev, [id]: { rating: 0, note: "", ...(prev || {})[id], ...patch } })),
    [setRaw]
  );
  const get = useCallback((id) => notes[String(id)] || { rating: 0, note: "" }, [notes]);

  return { notes, get, setNote };
}

/* --------------------------------------------------------- Recently viewed */
export const RECENT_KEY = "recify_recent";

export function useRecentlyViewed() {
  const [raw, setRaw] = useStoredState(RECENT_KEY, []);
  const recent = Array.isArray(raw) ? raw : [];

  const push = useCallback(
    (summary) => {
      if (!summary?.id) return;
      setRaw((prev) =>
        [summary, ...(Array.isArray(prev) ? prev : []).filter((r) => r.id !== summary.id)].slice(0, 12)
      );
    },
    [setRaw]
  );
  const clear = useCallback(() => setRaw([]), [setRaw]);

  return { recent, push, clear };
}

/* ------------------------------------------------------------ Meal planner */
export const DAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

export function usePlanner() {
  const [raw, setRaw] = useStoredState("recify_planner", {});
  const plan = raw && typeof raw === "object" ? raw : {};

  const addTo = useCallback(
    (day, summary) =>
      setRaw((prev) => ({
        ...(prev || {}),
        [day]: [...((prev || {})[day] || []), { ...summary, uid: uid() }],
      })),
    [setRaw]
  );
  const move = useCallback(
    (entryUid, fromDay, toDay) =>
      setRaw((prev) => {
        const next = { ...(prev || {}) };
        const entry = (next[fromDay] || []).find((e) => e.uid === entryUid);
        if (!entry || fromDay === toDay) return prev;
        next[fromDay] = next[fromDay].filter((e) => e.uid !== entryUid);
        next[toDay] = [...(next[toDay] || []), entry];
        return next;
      }),
    [setRaw]
  );
  const removeEntry = useCallback(
    (day, entryUid) =>
      setRaw((prev) => ({
        ...(prev || {}),
        [day]: ((prev || {})[day] || []).filter((e) => e.uid !== entryUid),
      })),
    [setRaw]
  );
  const clear = useCallback(() => setRaw({}), [setRaw]);

  return { plan, addTo, move, removeEntry, clear };
}
